import React, { useState, useEffect } from 'react';
import { ArrowLeft, Bot, Users, ShoppingCart, TrendingUp, Zap, Send } from 'lucide-react';
import SalesChatList from './SalesChatList';
import SalesChatView from './SalesChatView';
import SalesChatInfo from './SalesChatInfo';
import SalesAnalytics from './SalesAnalytics';
import { socket } from '../chat/socket';

const API_URL = process.env.REACT_APP_API_URL;

function SalesChatDashboard({ setActiveComponent }) {
  const user_id = 1; // Testing user ID
  
  const [selectedChat, setSelectedChat] = useState(null);
  const [chats, setChats] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Auto follow-up state
  const [autoFollowUpEnabled, setAutoFollowUpEnabled] = useState(false);
  const [followUpStats, setFollowUpStats] = useState({
    sent_today: 0,
    last_check: null
  });
  const [followUpNotification, setFollowUpNotification] = useState(null);

  // Fetch analytics on mount
  useEffect(() => {
    fetchAnalytics();
  }, []);

  // Socket.IO event listeners
  useEffect(() => {
    // Join admin sales chat list room
    socket.emit('join_admin_sales_chat_list');

    // Listen for chat updates
    socket.on('sales_chat_updated', handleChatUpdate);
    socket.on('new_sales_message', handleNewMessage);
    socket.on('sales_bot_toggled', handleBotToggle);

    return () => {
      socket.emit('leave_admin_sales_chat_list');
      socket.off('sales_chat_updated', handleChatUpdate);
      socket.off('new_sales_message', handleNewMessage);
      socket.off('sales_bot_toggled', handleBotToggle);
    };
  }, []);

  // Auto follow-up polling
  useEffect(() => {
    if (!autoFollowUpEnabled) return;

    console.log('[AutoFollowUp] Enabled - starting polling');

    // Check immediately
    checkAndSendFollowUps();

    // Then check every 2 minutes
    const interval = setInterval(() => {
      console.log('[AutoFollowUp] Running scheduled check...');
      checkAndSendFollowUps();
    }, 2 * 60 * 1000); // 2 minutes

    return () => {
      console.log('[AutoFollowUp] Disabled - stopping polling');
      clearInterval(interval);
    };
  }, [autoFollowUpEnabled]);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sales/analytics/dashboard?days=7`);
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const checkAndSendFollowUps = async () => {
    try {
      console.log('[AutoFollowUp] Fetching chats needing follow-up...');
      
      // Get list of chats needing follow-up
      const response = await fetch(`${API_URL}/api/sales/chats-needing-followup`);
      const data = await response.json();

      console.log(`[AutoFollowUp] Found ${data.count} chats needing follow-up`);

      if (data.count === 0) {
        setFollowUpStats(prev => ({
          ...prev,
          last_check: new Date().toLocaleTimeString('es-PE')
        }));
        return;
      }

      // Send follow-ups
      let sent_count = 0;
      
      for (const chat of data.chats) {
        try {
          console.log(`[AutoFollowUp] Sending ${chat.tier} follow-up to ${chat.phone_number}...`);
          
          const sendResponse = await fetch(
            `${API_URL}/api/sales/send-followup/${chat.chat_id}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ tier: chat.tier })
            }
          );

          const result = await sendResponse.json();

          if (result.success) {
            sent_count++;
            console.log(`[AutoFollowUp] ✓ Sent ${chat.tier} follow-up to ${chat.phone_number}`);
            
            // Show notification
            showFollowUpNotification(chat, chat.tier);
          } else {
            console.error(`[AutoFollowUp] ✗ Failed to send to ${chat.phone_number}:`, result.error);
          }
          
          // Small delay between sends
          await new Promise(resolve => setTimeout(resolve, 1000));
          
        } catch (error) {
          console.error(`[AutoFollowUp] Error sending to ${chat.phone_number}:`, error);
        }
      }

      // Update stats
      setFollowUpStats(prev => ({
        sent_today: prev.sent_today + sent_count,
        last_check: new Date().toLocaleTimeString('es-PE')
      }));

      console.log(`[AutoFollowUp] ✓ Completed - sent ${sent_count} follow-ups`);
      
    } catch (error) {
      console.error('[AutoFollowUp] Error in checkAndSendFollowUps:', error);
    }
  };

  const showFollowUpNotification = (chat, tier) => {
    const tierLabels = {
      '15min': '15 min reminder',
      '2hr': '2hr incentive (S/5 discount)',
      '23hr': '23hr final offer'
    };

    const notification = {
      phone: chat.phone_number,
      tier: tierLabels[tier] || tier,
      products: chat.interested_products?.map(p => p.title).join(', ') || 'producto',
      timestamp: new Date().toLocaleTimeString('es-PE')
    };

    setFollowUpNotification(notification);

    // Auto-hide after 5 seconds
    setTimeout(() => {
      setFollowUpNotification(null);
    }, 5000);
  };

  const handleChatUpdate = (chatData) => {
    setChats(prevChats => {
      const existingIndex = prevChats.findIndex(c => c.id === chatData.id);
      if (existingIndex >= 0) {
        // Update existing chat
        const updated = [...prevChats];
        updated[existingIndex] = { ...updated[existingIndex], ...chatData };
        // Move to top
        const [chat] = updated.splice(existingIndex, 1);
        return [chat, ...updated];
      } else {
        // Add new chat
        return [chatData, ...prevChats];
      }
    });
  };

  const handleNewMessage = (messageData) => {
    // Update chat list preview
    setChats(prevChats => 
      prevChats.map(chat => 
        chat.id === messageData.chat_id 
          ? { ...chat, last_message: messageData.content, unread: messageData.direction === 'inbound' }
          : chat
      )
    );

    // Refresh analytics
    fetchAnalytics();
  };

  const handleBotToggle = (data) => {
    setChats(prevChats =>
      prevChats.map(chat =>
        chat.id === data.chat_id
          ? { ...chat, is_bot_active: data.is_bot_active }
          : chat
      )
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ========== EMBEDDED CSS STYLES ========== */}
      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
      {/* ========== END EMBEDDED CSS ========== */}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setActiveComponent('OptionsAdmin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Sales WhatsApp Dashboard
                </h1>
                <p className="text-sm text-gray-500">
                  Gestiona conversaciones y ventas por WhatsApp
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Auto Follow-Up Toggle */}
              <div className="flex items-center space-x-2 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
                <Zap className={`w-4 h-4 ${autoFollowUpEnabled ? 'text-orange-500' : 'text-gray-400'}`} />
                <span className="text-sm font-medium text-gray-700">Auto Follow-Up:</span>
                <button
                  onClick={() => setAutoFollowUpEnabled(!autoFollowUpEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    autoFollowUpEnabled ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      autoFollowUpEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Follow-Up Stats */}
              {autoFollowUpEnabled && (
                <div className="px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Send className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-900">
                      {followUpStats.sent_today} enviados hoy
                    </span>
                  </div>
                  {followUpStats.last_check && (
                    <p className="text-xs text-orange-600 mt-0.5">
                      Último check: {followUpStats.last_check}
                    </p>
                  )}
                </div>
              )}

              {/* Bot Status */}
              <div className="flex items-center space-x-2 px-3 py-1 bg-green-50 text-green-700 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">Bot Activo</span>
              </div>
            </div>
          </div>
        </div>

        {/* Analytics Bar */}
        {analytics && <SalesAnalytics analytics={analytics} />}
      </div>

      {/* Follow-Up Notification */}
      {followUpNotification && (
        <div className="fixed top-20 right-6 z-50 animate-slide-in-right">
          <div className="bg-white border-l-4 border-orange-500 rounded-lg shadow-lg p-4 max-w-sm">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Send className="w-5 h-5 text-orange-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  ✅ Follow-up enviado
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {followUpNotification.phone}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Tier: {followUpNotification.tier}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {followUpNotification.timestamp}
                </p>
              </div>
              <button
                onClick={() => setFollowUpNotification(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex h-[calc(100vh-180px)]">
        {/* Chat List - Left Sidebar */}
        <div className="w-96 bg-white border-r border-gray-200 flex flex-col">
          <SalesChatList
            chats={chats}
            setChats={setChats}
            selectedChat={selectedChat}
            onSelectChat={setSelectedChat}
            loading={loading}
            setLoading={setLoading}
          />
        </div>

        {/* Chat View - Center */}
        <div className="flex-1 flex flex-col bg-gray-50">
          {selectedChat ? (
            <SalesChatView
              chat={selectedChat}
              userId={user_id}
              onChatUpdate={(updatedChat) => setSelectedChat(updatedChat)}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Bot className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Selecciona una conversación
                </h3>
                <p className="text-sm text-gray-500">
                  Elige un chat para ver los mensajes y gestionar la venta
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Chat Info - Right Sidebar */}
        {selectedChat && (
          <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto">
            <SalesChatInfo
              chat={selectedChat}
              userId={user_id}
              onChatUpdate={(updatedChat) => setSelectedChat(updatedChat)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default SalesChatDashboard;